import { type CSSProperties, type FormEvent, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import BrandLogo from '../../components/BrandLogo'
import { publicRescheduleApi, type RescheduleSlot } from '../../api/rescheduleOffers'
import { ApiError } from '../../api/client'
import { Button, Callout, Card, CardContent, TextField } from '../../components/ui'

/**
 * R3-16 — pagina pubblica `/r/:token` (fuori dal login): dopo un appuntamento saltato il cliente riceve un link, chiede un
 * codice via email e sceglie una delle tre fasce libere dell'agente. Il token si tiene solo in sessionStorage e si toglie
 * subito dalla barra degli indirizzi; alle API va in un header.
 */
const PAGE_STYLE: CSSProperties = {
  minHeight: '100svh',
  paddingTop: 'var(--md-safe-top)',
  paddingBottom: 'var(--md-safe-bottom)',
}
const STORAGE_KEY = 'ecrm_reschedule_link'

type Step = 'loading' | 'invalid' | 'otp' | 'slots' | 'done'

function readToken(fromUrl: string | undefined): string {
  if (fromUrl) {
    try { sessionStorage.setItem(STORAGE_KEY, fromUrl) } catch { /* ignore */ }
    window.history.replaceState(null, '', window.location.pathname.replace(/\/r\/.*$/, '/r'))
    return fromUrl
  }
  try { return sessionStorage.getItem(STORAGE_KEY) ?? '' } catch { return '' }
}

function messageOf(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback
}

function isTerminal(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 404 || e.status === 410 || e.status === 423)
}

const dayFormat = new Intl.DateTimeFormat('it-IT', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Rome' })
const timeFormat = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' })

export function ReschedulePage() {
  const params = useParams<{ token?: string }>()
  const [token] = useState(() => readToken(params.token))
  const [step, setStep] = useState<Step>('loading')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [emailHint, setEmailHint] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [session, setSession] = useState<string | null>(null)
  const [slots, setSlots] = useState<RescheduleSlot[]>([])
  const [chosen, setChosen] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  function fail(e: unknown, fallback: string) {
    if (isTerminal(e)) {
      setMessage(messageOf(e, 'Link non valido.'))
      setStep('invalid')
      try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
    } else if (e instanceof ApiError && e.status === 401) {
      setSession(null); setStep('otp'); setError(messageOf(e, fallback))
    } else {
      setError(messageOf(e, fallback))
    }
  }

  useEffect(() => {
    if (!token) { setMessage('Link non valido.'); setStep('invalid'); return }
    publicRescheduleApi.open(token)
      .then(r => { setEmailHint(r.emailHint); setStep('otp') })
      .catch(e => fail(e, 'Link non valido.'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  async function sendCode() {
    setBusy(true); setError(''); setInfo('')
    try {
      await publicRescheduleApi.sendOtp(token)
      setInfo(emailHint ? `Ti abbiamo inviato un codice a ${emailHint}.` : 'Ti abbiamo inviato un codice via email.')
    } catch (e) { fail(e, 'Invio del codice non riuscito.') } finally { setBusy(false) }
  }

  async function verify(ev: FormEvent) {
    ev.preventDefault()
    setBusy(true); setError('')
    try {
      const r = await publicRescheduleApi.verifyOtp(token, code)
      const res = await publicRescheduleApi.slots(token, r.session)
      setSession(r.session); setSlots(res.slots); setCode(''); setInfo(''); setStep('slots')
    } catch (e) { fail(e, 'Codice non corretto.') } finally { setBusy(false) }
  }

  async function choose(slot: RescheduleSlot) {
    if (!session) return
    setBusy(true); setError('')
    try {
      const r = await publicRescheduleApi.choose(token, session, slot.index)
      setChosen(r.start)
      try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
      setSession(null); setStep('done')
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError(messageOf(e, 'Questa fascia non è più libera.'))
        try { setSlots((await publicRescheduleApi.slots(token, session)).slots) } catch { /* ignore */ }
      } else {
        fail(e, 'Scelta non riuscita.')
      }
    } finally { setBusy(false) }
  }

  return (
    <div style={PAGE_STYLE} className="bg-body d-flex justify-content-center">
      <div className="w-100 p-3" style={{ maxWidth: 640 }}>
        <Card variant="outlined" padding="md">
          <CardContent>
            <div className="text-center mb-4"><BrandLogo height={56} /></div>

            {step === 'loading' && <p className="text-muted">Verifica del link…</p>}
            {step === 'invalid' && <Callout tone="error">{message}</Callout>}
            {step === 'done' && (
              <Callout tone="success" title="Appuntamento fissato">
                {chosen
                  ? `Ti aspettiamo ${dayFormat.format(new Date(chosen))} alle ${timeFormat.format(new Date(chosen))}. Il tuo consulente ti confermerà i dettagli.`
                  : 'Il tuo consulente ti confermerà i dettagli.'}
              </Callout>
            )}

            {error && step !== 'invalid' && <Callout tone="error" className="mb-3">{error}</Callout>}
            {info && <Callout tone="info" className="mb-3">{info}</Callout>}

            {step === 'otp' && (
              <form onSubmit={e => void verify(e)}>
                <h1 className="md-typescale-title-large mb-2">Scegli un nuovo appuntamento</h1>
                <p className="md-typescale-body-medium">
                  Per sicurezza ti mandiamo un codice di 6 cifre {emailHint ? <>all&apos;indirizzo <strong>{emailHint}</strong></> : 'via email'}.
                </p>
                <Button variant="tonal" icon="mail" loading={busy} onClick={() => void sendCode()}>Invia il codice</Button>
                <div className="mt-3">
                  <TextField
                    label="Codice"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={e => setCode(e.currentTarget.value.replace(/\D/g, ''))}
                  />
                </div>
                <Button variant="filled" type="submit" className="mt-3" loading={busy} disabled={code.length !== 6}>Continua</Button>
              </form>
            )}

            {step === 'slots' && (
              <div>
                <h1 className="md-typescale-title-large mb-2">Scegli quando</h1>
                {slots.length === 0 ? (
                  <Callout tone="warning">Le fasce proposte non sono più libere. Il tuo consulente ti ricontatterà.</Callout>
                ) : (
                  <>
                    <p className="md-typescale-body-medium">Queste sono le fasce libere del tuo consulente. Tocca quella che preferisci.</p>
                    <div className="d-flex flex-column gap-2">
                      {slots.map(s => (
                        <Button key={s.index} variant="outlined" loading={busy} onClick={() => void choose(s)}>
                          {`${dayFormat.format(new Date(s.start))}, ${timeFormat.format(new Date(s.start))}`}
                        </Button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
